import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Settings } from 'lucide-react';
interface OtpEntryScreenProps {
  email: string;
  onSubmit: (code: string) => void;
  onBack: () => void;
  onSkip?: () => void;
}
export function OtpEntryScreen({
  email,
  onSubmit,
  onBack,
  onSkip
}: OtpEntryScreenProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);
  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split('');
      const newCode = [...code];
      pastedCode.forEach((char, i) => {
        if (index + i < 6) newCode[index + i] = char;
      });
      setCode(newCode);
      // Focus next empty input or last input
      const nextEmptyIndex = newCode.findIndex(c => c === '');
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();
      return;
    }
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    // Move to next input automatically
    if (value !== '' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length === 6) {
      onSubmit(fullCode);
    }
  };
  const isComplete = code.every(c => c !== '');
  return <div className="relative w-full h-screen overflow-hidden bg-slate-900" data-id="element-184">
      {/* Background */}
      <div className="absolute inset-0 z-0" data-id="element-185">
        <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1920&q=80" alt="Interior background" className="w-full h-full object-cover opacity-20 blur-sm" data-id="element-186" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/70" data-id="element-187" />
      </div>

      {/* Header - Higher z-index */}
      <div className="absolute top-6 left-6 right-6 z-50 flex items-center justify-between" data-id="element-188">
        <button onClick={onBack} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors" data-id="element-189">
          <ArrowLeft className="w-5 h-5" data-id="element-190" />
          <span className="text-sm" data-id="element-191">Back</span>
        </button>
        <button className="p-3 rounded-full bg-slate-800/50 backdrop-blur-sm border border-white/10 hover:bg-slate-700/50 transition-colors" data-id="element-192">
          <Settings className="w-6 h-6 text-white" data-id="element-193" />
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center h-full" data-id="element-194">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="bg-slate-800/80 backdrop-blur-xl rounded-2xl p-12 border border-white/10 shadow-2xl max-w-md w-full" data-id="element-195">
          <form onSubmit={handleSubmit} data-id="element-196">
            <h2 className="text-xl text-white/80 text-center mb-2 uppercase tracking-wider" data-id="element-197">
              Enter Verification Code
            </h2>
            <p className="text-white/50 text-center text-sm mb-8" data-id="element-198">
              Sent to {email}
            </p>

            <div className="flex justify-between gap-2 mb-8" data-id="element-199">
              {code.map((digit, index) => <input key={index} ref={el => inputRefs.current[index] = el} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} // Allow paste
            value={digit} onChange={e => handleChange(index, e.target.value.replace(/[^0-9]/g, ''))} onKeyDown={e => handleKeyDown(index, e)} className="w-12 h-14 bg-slate-700/50 border border-white/10 rounded-lg text-white text-2xl text-center focus:outline-none focus:border-cyan-500 transition-colors" data-id="element-200" />)}
            </div>

            <button type="submit" disabled={!isComplete} className="w-full px-8 py-3 bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 disabled:from-slate-700 disabled:to-slate-600 text-white font-medium rounded-lg transition-all disabled:cursor-not-allowed disabled:opacity-50 uppercase tracking-wider" data-id="element-201">
              Verify
            </button>

            {onSkip && <button type="button" onClick={onSkip} className="mt-4 text-white/30 hover:text-white/60 text-sm transition-colors mx-auto block" data-id="element-202">
                Guest access →
              </button>}
          </form>
        </motion.div>
      </div>
    </div>;
}